import { MIN_OBSERVATIONS_FOR_VERDICT, MIN_INTERVAL_DAYS } from './thresholds'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * @typedef {object} MomentFill
 * @property {string} respondentId - De deelnemer of het huishouden dat invulde
 * @property {string} momentId - Het meetmoment
 * @property {number|null} filledAtMs - Wanneer, in milliseconden
 */

/**
 * De waargenomen tussentijden in dagen: per invuller de afstand tussen twee
 * opeenvolgende meetmomenten.
 *
 * Nadrukkelijk per invuller, nooit over twee mensen heen — de afstand tussen
 * de nulmeting van de één en de nameting van de ander zegt niets.
 *
 * @param {Array<MomentFill>} [fills]
 * @returns {Array<number>} Tussentijden in hele dagen, ongesorteerd
 */
const collectIntervalObservations = (fills) => {
    /** @type {Record<string, Record<string, number>>} */
    const earliestPerRespondent = {}

    ;(fills || []).forEach((entry) => {
        if (!entry || !entry.respondentId || !entry.momentId) return
        const ms = entry.filledAtMs
        if (typeof ms !== 'number' || !Number.isFinite(ms)) return

        const perMoment = earliestPerRespondent[entry.respondentId] || {}
        const known = perMoment[entry.momentId]
        // De eerste invulling telt: een herziening later verandert niet wanneer
        // dit meetmoment aan de beurt was.
        if (known === undefined || ms < known) perMoment[entry.momentId] = ms
        earliestPerRespondent[entry.respondentId] = perMoment
    })

    const gaps = []
    Object.values(earliestPerRespondent).forEach((perMoment) => {
        const times = Object.values(perMoment).sort((a, b) => a - b)
        for (let i = 1; i < times.length; i += 1) {
            gaps.push(Math.round((times[i] - times[i - 1]) / DAY_MS))
        }
    })

    return gaps
}

/**
 * De gebruikelijke tussentijd van één onderzoek, afgeleid uit wat er in dat
 * onderzoek al is ingevuld — of `null` zodra we het niet met droge ogen kunnen
 * zeggen.
 *
 * `null` is hier een volwaardig antwoord, geen gebrek: dan toont het scherm
 * hoe lang iets al openstaat zónder er een oordeel aan te hangen. Zie
 * `thresholds.js` voor waarom we niet terugvallen op een vaste termijn.
 *
 * @param {Array<MomentFill>} [fills] - Alle invullingen binnen één onderzoek
 * @returns {number|null} Mediane tussentijd in dagen, of `null` bij te weinig grond
 */
const inferIntervalDays = (fills) => {
    const gaps = collectIntervalObservations(fills)
    if (gaps.length < MIN_OBSERVATIONS_FOR_VERDICT) return null

    const sorted = [...gaps].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]

    return median >= MIN_INTERVAL_DAYS ? median : null
}

export default inferIntervalDays
export { collectIntervalObservations }
